import { useState } from "react";
import { Box, Button, Drawer, IconButton, Typography } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import dayjs, { Dayjs } from "dayjs";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import LeadsMobileDateRangeDrawer from "./LeadsMobileDateRangeDrawer";
import IconWithIndicator from "./IconWithIndicator";
import { FilterRow } from "./LeadsMobileFilterBar";
import {
  IP_FILTER_OPTIONS,
  MIN_SCORE_FILTER_OPTIONS,
  MULTI_BRAND_FILTER_OPTIONS,
  QUALIFIED_FILTER_OPTIONS,
} from "../constants";
import { colors, typography, spacing } from "../../../../assets/styles/tokens";

interface AnalyticsMobileFilterBarProps {
  data: LeadsManagementData;
}

function toDayjs(value: string): Dayjs | null {
  return value ? dayjs(value) : null;
}

function formatDisplayDate(value: string): string {
  const parsed = toDayjs(value);
  return parsed ? parsed.format("DD/MM/YYYY") : "—";
}

// Analytics' mobile filter bar — same date-range drawer as the Leads tab
// (LeadsMobileDateRangeDrawer) and a Filter by drawer where only Brand
// applies to Analytics. No keyword search, matching desktop Analytics.
export default function AnalyticsMobileFilterBar({ data }: AnalyticsMobileFilterBarProps) {
  const { start, end, setStart, setEnd, brand, setBrand, brandOptions, isLoading } = data;
  const [dateDrawerOpen, setDateDrawerOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  // Brand only takes effect on Apply, same as the Leads tab's filter drawer.
  const [draftBrand, setDraftBrand] = useState(brand);

  function openFilterDrawer() {
    setDraftBrand(brand);
    setFilterDrawerOpen(true);
  }

  function applyFilters() {
    setBrand(draftBrand);
    setFilterDrawerOpen(false);
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: spacing.Spacing.md.$value, mt: spacing.Spacing.lg.$value }}>
      <Button
        onClick={() => setDateDrawerOpen(true)}
        endIcon={<KeyboardArrowDownIcon />}
        disabled={isLoading}
        sx={{
          flex: 1,
          height: 44,
          justifyContent: "space-between",
          border: `1px solid ${colors.GrayLightMode[200].$value}`,
          borderRadius: spacing.Radius.md.$value,
          px: spacing.Spacing.lg.$value,
          fontFamily: typography.Font_Family.$value,
          fontWeight: typography.Font_weight.Semibold.$value,
          fontSize: typography.Size.Text.sm.$value,
          lineHeight: typography.Line_height.text.sm.$value,
          letterSpacing: "0%",
          verticalAlign: "middle",
          color: colors.GrayLightMode[700].$value,
          textTransform: "none",
        }}
      >
        {formatDisplayDate(start)} - {formatDisplayDate(end)}
      </Button>

      <IconButton
        onClick={openFilterDrawer}
        sx={{ width: 44, height: 44, borderRadius: spacing.Radius.md.$value, color: colors.GrayLightMode[700].$value }}
      >
        <IconWithIndicator active={brand !== "All"}>
          <TuneIcon fontSize="medium" />
        </IconWithIndicator>
      </IconButton>

      <LeadsMobileDateRangeDrawer
        open={dateDrawerOpen}
        onClose={() => setDateDrawerOpen(false)}
        start={start}
        end={end}
        onApply={(nextStart, nextEnd) => {
          setStart(nextStart);
          setEnd(nextEnd);
        }}
      />

      <Drawer
        anchor="bottom"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", pt: spacing.Spacing.lg.$value }}>
          <Box sx={{ width: 36, height: 4, borderRadius: spacing.Radius.md.$value, bgcolor: colors.GrayLightMode[200].$value }} />
        </Box>

        <Box
          sx={{
            px: spacing.Spacing["2xl"].$value,
            pt: spacing.Spacing.lg.$value,
            pb: spacing.Spacing["2xl"].$value,
            display: "flex",
            flexDirection: "column",
            gap: spacing.Spacing.xl.$value,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography
              sx={{
                fontFamily: typography.Font_Family.$value,
                fontWeight: typography.Font_weight.Semibold.$value,
                fontSize: typography.Size.Text.md.$value,
                color: colors.GrayLightMode[900].$value,
              }}
            >
              Filter by
            </Typography>
            <Typography
              onClick={draftBrand !== "All" ? () => setDraftBrand("All") : undefined}
              sx={{
                fontFamily: typography.Font_Family.$value,
                fontWeight: typography.Font_weight.Semibold.$value,
                fontSize: typography.Size.Text.sm.$value,
                cursor: draftBrand !== "All" ? "pointer" : "default",
              }}
            >
              Clear All
            </Typography>
          </Box>

          <FilterRow label="Brand Name" value={draftBrand} onChange={setDraftBrand} options={brandOptions} />
          {/* Leads-tab-only filters — the Analytics aggregations don't take these params. */}
          <FilterRow label="Qualified By Brand" value="All" onChange={() => {}} options={QUALIFIED_FILTER_OPTIONS} disabled />
          <FilterRow label="IP Address" value="All" onChange={() => {}} options={IP_FILTER_OPTIONS} disabled />
          <FilterRow label="Multi Brand" value="All" onChange={() => {}} options={MULTI_BRAND_FILTER_OPTIONS} disabled />
          <FilterRow label="Minimum Lead Score" value="0" onChange={() => {}} options={MIN_SCORE_FILTER_OPTIONS} disabled />

          <Button
            fullWidth
            variant="contained"
            onClick={applyFilters}
            disabled={draftBrand === brand}
            sx={{
              mt: spacing.Spacing.md.$value,
              borderRadius: spacing.Radius.md.$value,
              bgcolor: colors.Brand[600].$value,
              "&:hover": { bgcolor: colors.Brand[700].$value },
              "&.Mui-disabled": {
                bgcolor: colors.GrayLightMode[100].$value,
                color: colors.GrayLightMode[400].$value,
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
}
